import { Autocomplete, Avatar, Box, FormControl, FormHelperText, Grid, InputAdornment, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useGetIds } from '~/modules/auth';
import { useGetMembersByLocationQuery, useGetMembersByNameQuery } from '~/services/member';
import { MemberNotRegisteredByLocation } from '~/services/types';

export type ContactType = Partial<{
  name: string;
  id: number;
  avatar: any;
}>;

type Props = {
  defaultOption: ContactType | null;
  shouldLoadAllMembers: boolean;
  onSelect: (option: ContactType) => void;
  error: string;
};

export function ContactNameAutocomplete({ onSelect, shouldLoadAllMembers, error, defaultOption }: Props) {
  const { locationId } = useGetIds();

  const [contactOption, setContactOption] = useState<ContactType | null>(null);
  const [allContactOptions, setAllContactOptions] = useState<readonly ContactType[]>([]);
  const [inputValue, setInputValue] = useState('');

  const { data: rawAllMembers, isPending: isAllMembersPending } = useGetMembersByLocationQuery<
    MemberNotRegisteredByLocation[]
  >({ locationId });
  const { data: rawMembers, isPending: isMembersPending } = useGetMembersByNameQuery<MemberNotRegisteredByLocation>({
    locationId,
    name: inputValue,
  });

  useEffect(() => {
    if (contactOption?.id && contactOption.name) {
      setInputValue(contactOption.name);
    }
  }, [contactOption]);

  useEffect(() => {
    if (defaultOption?.id && defaultOption.name) {
      setContactOption(defaultOption);
    }
  }, [defaultOption]);

  useEffect(() => {
    if (shouldLoadAllMembers && rawAllMembers && !isAllMembersPending && inputValue === '') {
      setAllContactOptions(
        rawAllMembers
          .filter((raw) => ['client', 'frozen', 'lead'].includes((raw.contact_type ?? '').toLowerCase()))
          .map((raw) => ({
            id: raw.member_id,
            name: raw.member_name,
            membership_status: raw.membership_status,
          })),
      );
    }
  }, [rawAllMembers, isAllMembersPending, shouldLoadAllMembers, inputValue]);

  useEffect(() => {
    if (rawMembers && !isMembersPending && inputValue !== '') {
      setAllContactOptions(
        rawMembers.map((raw) => ({
          id: raw.member_id,
          name: raw.member_name,
          membership_status: raw.membership_status,
        })),
      );
    }
  }, [rawMembers, isMembersPending, inputValue]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Autocomplete
            value={contactOption}
            options={allContactOptions}
            autoComplete
            includeInputInList
            filterSelectedOptions
            loading={isMembersPending && !!inputValue}
            getOptionLabel={(option) => {
              return option.name ?? '';
            }}
            isOptionEqualToValue={(option, value) => {
              const condition = option?.id === value?.id;
              return condition;
            }}
            noOptionsText="No contacts"
            onChange={(_event, newValue: ContactType | null) => {
              let newContactOption = null;

              if (newValue?.id) {
                onSelect(newValue);
                newContactOption = { ...newValue };
              } else {
                onSelect({ id: undefined, name: '' });
              }
              setContactOption(newContactOption);
            }}
            inputValue={inputValue}
            onInputChange={(_event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  fullWidth
                  placeholder=""
                  InputProps={{
                    ...params.InputProps,
                    error: !!error,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Avatar sx={{ width: 25, height: 25 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              );
            }}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  <Grid container alignItems="center">
                    <Grid item sx={{ display: 'flex', width: 44 }}>
                      <Avatar sx={{ width: 35, height: 35 }} />
                    </Grid>
                    <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                      <Box component="span">{option.name}</Box>
                    </Grid>
                  </Grid>
                </li>
              );
            }}
          />
          {!!error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
      </Grid>
    </Grid>
  );
}
